import { CARDS, type Card, type VocabCard, type ConjCard, type SentenceCard } from "./deck";
import type { Direction } from "./store";

export interface MCQuestion {
  type: "mc";
  card: VocabCard;
  prompt: string;
  correctAnswer: string;
  options: string[];
  speakText: string | null;
  dir: "hr-de" | "de-hr";
}

export interface ConjQuestion {
  type: "conj";
  card: ConjCard;
  prompt: string;
  pronoun: string;
  correctAnswer: string;
  options: string[];
  speakText: string;
}

export interface SentenceQuestion {
  type: "sentence";
  card: SentenceCard;
  prompt: string;
  tokens: string[];
  correctOrder: string[];
  speakText: string;
}

export type Question = MCQuestion | ConjQuestion | SentenceQuestion;

function shuffle<T>(arr: T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function resolveDir(setting: Direction): "hr-de" | "de-hr" {
  if (setting === "mix") return Math.random() < 0.5 ? "hr-de" : "de-hr";
  return setting;
}

function pickDistractors(pool: string[], correct: string, count: number): string[] {
  const candidates = pool.filter(s => s !== correct);
  const picked = shuffle(candidates).slice(0, count);
  while (picked.length < count) picked.push("—");
  return picked;
}

function isQuestion(c: VocabCard): boolean {
  return c.hr.trimEnd().endsWith("?") || c.de.trimEnd().endsWith("?");
}

export function buildMC(card: VocabCard, dir: Direction): MCQuestion {
  const direction = resolveDir(dir);
  const promptIsQuestion = isQuestion(card);
  const allVocab = CARDS.filter(c => "hr" in c && "de" in c) as VocabCard[];
  const sameCat = allVocab.filter(c => c.cat === card.cat && isQuestion(c) === promptIsQuestion);
  const sameType = allVocab.filter(c => isQuestion(c) === promptIsQuestion);
  const sourcePool = sameCat.length >= 4 ? sameCat : sameType;
  let pool: string[];
  let prompt: string;
  let correct: string;
  let speakText: string | null;
  if (direction === "hr-de") {
    prompt = card.hr;
    correct = card.de;
    pool = sourcePool.map(c => c.de);
    speakText = card.hr;
  } else {
    prompt = card.de + (card.hint ? ` ${card.hint}` : "");
    correct = card.hr;
    pool = sourcePool.map(c => c.hr);
    speakText = null;
  }
  const distractors = pickDistractors(pool, correct, 3);
  const options = shuffle([correct, ...distractors]);
  return {
    type: "mc",
    card,
    prompt,
    correctAnswer: correct,
    options,
    speakText,
    dir: direction,
  };
}

export function buildConj(card: ConjCard): ConjQuestion {
  const verbCards = CARDS.filter(
    c => c.cat === "Konjugacija" && "verb" in c && c.verb === card.verb
  ) as ConjCard[];
  const otherForms = verbCards.filter(c => c.id !== card.id).map(c => c.answer);
  const pool = otherForms.length >= 3
    ? otherForms
    : (CARDS.filter(c => c.cat === "Konjugacija") as ConjCard[]).map(c => c.answer);
  const distractors = pickDistractors(pool, card.answer, 3);
  const options = shuffle([card.answer, ...distractors]);
  return {
    type: "conj",
    card,
    prompt: `${card.verb} — ${card.pron}`,
    pronoun: card.pron,
    correctAnswer: card.answer,
    options,
    speakText: `${card.pron} ${card.answer}`,
  };
}

export function buildSentence(card: SentenceCard): SentenceQuestion {
  return {
    type: "sentence",
    card,
    prompt: card.de,
    tokens: shuffle(card.tokens),
    correctOrder: card.tokens.slice(),
    speakText: card.hr,
  };
}

export function buildQuestion(card: Card, dir: Direction): Question {
  if ("isConj" in card && card.isConj) return buildConj(card);
  if ("isSentence" in card && card.isSentence) return buildSentence(card);
  return buildMC(card as VocabCard, dir);
}

export function checkAnswer(q: Question, answer: string | string[]): boolean {
  if (q.type === "sentence") {
    if (!Array.isArray(answer)) return false;
    if (answer.length !== q.correctOrder.length) return false;
    return answer.every((t, i) => t === q.correctOrder[i]);
  }
  if (typeof answer !== "string") return false;
  return answer === q.correctAnswer;
}
